import React from 'react';
import { useMediaQuery, Theme, Typography } from '@mui/material';
import { DataGridPro, DataGridProProps, useGridApiContext, GridRenderEditCellParams, getGridDateOperators, GRID_CHECKBOX_SELECTION_FIELD } from '@mui/x-data-grid-pro';
import { deepmerge } from '@mui/utils';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const ACTION_ICON_FIELDS = new Set([
  'actions',
  'edit',
  'archive',
  'reactivate',
  'delete',
  'duplicate',
  'view',
  'impersonate',
  'access',
  'resend_setup_email',
  'complete',
]);

// ===========================|| RESPONSIVE DATA GRID - HANDLE DATE PICKER AND MOBILE SETTINGS ||=========================== //

export const ResponsiveDataGrid: React.FC<DataGridProProps> = ({ slotProps = {}, sx, pinnedColumns, initialState, ...props }) => {
  
  const isLargeScreen = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const resolvedPinnedColumns: DataGridProProps['pinnedColumns'] = isLargeScreen
    ? {
        ...pinnedColumns,
        left: Array.from(new Set([GRID_CHECKBOX_SELECTION_FIELD, 'actions']))
      }
    : { left: [] };


  const handleMobileDatePickerClick = (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    const inputElement = e.currentTarget.querySelector('input') as HTMLInputElement | null;

    if (inputElement && (inputElement.type === 'date' || inputElement.type === 'datetime-local')) {
      e.stopPropagation();
      inputElement.focus();
      if (typeof inputElement.showPicker === 'function') {
        try {
          inputElement.showPicker();
        } catch (err) {
          console.warn("Native picker failed to open:", err);
        }
      }
    }
  };

  const defaultSlotProps: DataGridProProps['slotProps'] = {
    panel: {
        sx: {
            // Prevent the filter panel width from overflowing
            maxWidth: '92vw', 
            margin: '0 auto',
            '& .MuiDataGrid-filterForm': {
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' }, // Stack vertically on mobile
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: 1,
            padding: 1,
            },

            '& .MuiDataGrid-filterFormValueInput, & .MuiDataGrid-filterFormColumnInput, & .MuiDataGrid-filterFormOperatorInput': {
            width: { xs: '100% !important', sm: 'auto' },
            },

            // Align "Remove filter" button on mobile
            '& .MuiDataGrid-filterFormDeleteIcon': {
            justifyContent: { xs: 'flex-end', sm: 'center' },
            marginTop: { xs: 1, sm: 0 },
            },
        },
    },
    // Filter date pickers should open on tap for mobile users
    baseTextField: {
      InputProps: {
        sx: { py: { xs: 0.5, sm: 0 } }
      },
      onClick: handleMobileDatePickerClick,
      onTouchStart: handleMobileDatePickerClick
    },
    cell: {
      onClick: (e: React.MouseEvent<HTMLDivElement>) => {
        const inputElement = e.currentTarget.querySelector('input') as HTMLInputElement | null;
        if (inputElement && (inputElement.type === 'date' || inputElement.type === 'datetime-local')) {
          handleMobileDatePickerClick(e);
        }
      },
      onTouchStart: (e: React.TouchEvent<HTMLDivElement>) => {
        const inputElement = e.currentTarget.querySelector('input') as HTMLInputElement | null;
        if (inputElement && (inputElement.type === 'date' || inputElement.type === 'datetime-local')) {
          handleMobileDatePickerClick(e);
        }
      }
    }
  };

  const mergedSlotProps = deepmerge(defaultSlotProps, slotProps); // Merge sx objects safely
  const mergedSx = deepmerge(
    {
      '& .MuiDataGrid-toolbarContainer': {
        flexWrap: 'nowrap',
        overflowX: 'auto',
        overflowY: 'hidden',
        width: '100%',
        minWidth: 0,
        paddingTop: '7px',
        paddingBottom: '8px',
        '& .MuiButton-root': {
          flexShrink: 0,
          whiteSpace: 'nowrap'
        }
      },
      '& .MuiTablePagination-root:last-child': {
        overflow: 'unset',
      }
    },
    sx || {}
  );

  const centeredColumns = props.columns?.map((col: any) => { // center the actions column and icon fields, not the 'actions' column itself
    if (col.type === 'actions' || ACTION_ICON_FIELDS.has(col.field)) {
      return {
        ...col,
        align: col.align ?? 'center',
        headerAlign: col.headerAlign ?? 'center',
      };
    }
    return col;
  });

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: { xs: 'block', md: 'none' }, px: 1, mb: 1, lineHeight: 1 }}
      >
        Swipe for more options →
      </Typography>
      <DataGridPro
        {...props}
        columns={centeredColumns ?? props.columns}
        slotProps={mergedSlotProps}
        sx={mergedSx}
        pinnedColumns={resolvedPinnedColumns}
        initialState={{
          ...initialState,
          pinnedColumns: resolvedPinnedColumns
        }}
      />
    </LocalizationProvider>
  );
};

const GridFilterDateTimePickerInput = (props: any) => {
  const { item, applyValue, focusElementRef } = props;

  return (
    <DateTimePicker
      value={item.value ? new Date(item.value) : null}
      onChange={(newValue) => {
        applyValue({ ...item, value: newValue ? newValue.toISOString() : '' });
      }}
      slotProps={{
        textField: {
          variant: 'standard',
          inputRef: focusElementRef,
          sx: {
            px: 1,
            height: '100%',
            display: 'flex',
            justify: 'center',
            '& .MuiInput-root': {
              height: '100%',
              fontSize: 'inherit',
              fontFamily: 'inherit',
              '&:before, &:after': {
                borderBottom: 'none !important', // Removes the standard input bottom border
              },
            },
            '& .MuiInput-input': {
              paddingTop: 0,
              paddingBottom: 0,
              height: '100%',
            },
            '& .MuiInputAdornment-root': {
              '& button': {
                padding: '2px',
                marginRight: '-4px',
              },
            },
          },
        },
      }}
    />
  );
};

// Filter dateTime picker uses a custom MUI DateTimePicker component (date time pickers are not supported natively in Firefox/Safari)
export const customDateTimeOperators = getGridDateOperators(true).map((operator) => ({
  ...operator,
  InputComponent: GridFilterDateTimePickerInput,
}));

// dateTime columns use a custom MUI DateTimePicker component as the inline editor (date time pickers are not supported natively in Firefox/Safari)
export const GridEditDateTimePickerCell = (props: GridRenderEditCellParams) => {
  const { id, field, value } = props;
  const apiRef = useGridApiContext();

  const handleChange = (newValue: any) => {
    apiRef.current.setEditCellValue({
      id,
      field,
      value: newValue ? new Date(newValue) : null,
    });
  };

  return (
    <DateTimePicker
      value={value ? new Date(value) : null}
      onChange={handleChange}
      slotProps={{
        textField: {
          variant: 'standard',
          fullWidth: true,
          sx: {
            px: 1,
            height: '100%',
            display: 'flex',
            justify: 'center',
            '& .MuiInput-root': {
              height: '100%',
              fontSize: 'inherit',
              fontFamily: 'inherit',
              '&:before, &:after': {
                borderBottom: 'none !important', // Removes the standard input bottom border
              },
            },
            '& .MuiInput-input': {
              paddingTop: 0,
              paddingBottom: 0,
              height: '100%',
            },
            '& .MuiInputAdornment-root': {
              '& button': {
                padding: '2px',
                marginRight: '-4px',
              },
            },
          },
        },
      }}
    />
  );
};